import React, { useState, useMemo } from 'react';
import { useStore } from '../../store/StoreContext';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { calculateSchedule, getStatusColor, generateId } from '../../utils/helpers';
import { Button } from '../../components/UI/Button';
import { Select } from '../../components/UI/Input';
import { useNavigate } from 'react-router-dom';

const extractErrorMessage = (error) => {
  if (!error) return 'Failed to archive project.';
  if (typeof error === 'string') return error;
  if (error.message) return error.message;

  try {
    return JSON.stringify(error);
  } catch {
    return 'Failed to archive project.';
  }
};

export const ProjectsPage = () => {
  const { state, addEntity, updateEntity, deleteEntity } = useStore();
  const [archivingId, setArchivingId] = useState(null);
  const [selectedBU, setSelectedBU] = useState('all');
  const navigate = useNavigate();

  const allProjects = useMemo(() => {
    return Object.values(state.projects || {})
      .filter((project) => project.status !== 'archived')
      .map((project) => calculateSchedule(project));
  }, [state.projects]);

  const filteredProjects = useMemo(() => {
    if (selectedBU === 'all') return allProjects;
    return allProjects.filter((project) => project.businessUnitId === selectedBU);
  }, [allProjects, selectedBU]);

  const businessUnits = Object.values(state.businessUnits || {});

  const handleArchive = async (project) => {
    if (!window.confirm(`Archive "${project.name}"? It will be moved to the Business Unit archive.`)) {
      return;
    }

    setArchivingId(project.id);

    try {
      // Use the original stored record rather than the calculated schedule.
      // This prevents computed fields from being sent to Supabase.
      const storedProject = state.projects[project.id] || project;
      await updateEntity('projects', {
        ...storedProject,
        status: 'archived'
      });
    } catch (error) {
      console.error('Archive project error:', error);
      alert(extractErrorMessage(error));
    } finally {
      setArchivingId(null);
    }
  };

  const handleCopyProject = async (project) => {
    const newProject = {
      ...project,
      id: generateId('p'),
      name: `${project.name} (Copy)`,
      created_at: new Date().toISOString(),
      status: 'active',
      workflows: (project.workflows || []).map((workflow) => ({
        ...workflow,
        id: generateId('wi'),
        tasks: (workflow.tasks || []).map((task) => ({
          ...task,
          id: generateId('pti')
        }))
      }))
    };

    try {
      await addEntity('projects', newProject);
    } catch (error) {
      console.error('Copy project error:', error);
      alert(extractErrorMessage(error));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full flex flex-col bg-base-900"
    >
      <div className="p-6 border-b border-base-700 bg-base-800 flex flex-wrap justify-between items-center gap-6 shrink-0 shadow-lg relative z-10">
        <div className="flex items-center gap-8">
          <div>
            <h1 className="text-2xl font-bold">Active Projects</h1>
            <p className="text-sm text-gray-400">
              Monitor and manage your running project portfolio.
            </p>
          </div>

          <div className="w-64">
            <Select
              label="Filter by Business Unit"
              value={selectedBU}
              onChange={(event) => setSelectedBU(event.target.value)}
              options={[
                { label: 'All Business Units', value: 'all' },
                ...businessUnits.map((businessUnit) => ({
                  label: businessUnit.name,
                  value: businessUnit.id
                }))
              ]}
            />
          </div>
        </div>

        <Button onClick={() => navigate('/builder')}>
          <SafeIcon name="Plus" className="mr-2" />
          Launch New Project
        </Button>
      </div>

      <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">
        {filteredProjects.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-500 py-20">
            <div className="w-20 h-20 bg-base-800 rounded-3xl flex items-center justify-center mb-6 border border-base-700 shadow-inner">
              <SafeIcon name="Folder" className="w-10 h-10 opacity-20" />
            </div>

            <p className="text-lg font-medium">
              {selectedBU === 'all'
                ? 'No active projects found.'
                : 'No projects found for this unit.'}
            </p>

            {selectedBU !== 'all' && (
              <button
                onClick={() => setSelectedBU('all')}
                className="mt-2 text-primary hover:underline text-sm font-bold uppercase tracking-widest"
              >
                Clear Filter
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 pb-10">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => {
                const businessUnit = state.businessUnits[project.businessUnitId];
                const isArchiving = archivingId === project.id;
                const variance = project.deadlineVariance;
                const isOverdue = variance < 0;
                const varianceAbs = Math.abs(variance);

                return (
                  <motion.div
                    layout
                    key={project.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-base-800 rounded-2xl border border-base-700 overflow-hidden hover:border-primary/50 transition-all shadow-xl flex flex-col group"
                  >
                    <div className="p-6 border-b border-base-700 bg-base-900/30 flex justify-between items-start gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div
                          className="w-3 h-12 rounded-full shadow-lg shrink-0"
                          style={{ backgroundColor: project.color }}
                        />

                        <div className="min-w-0">
                          <h3 className="text-lg font-bold truncate group-hover:text-primary transition-colors">
                            {project.name || 'Untitled Project'}
                          </h3>

                          <div className="flex items-center gap-2 mt-1">
                            <div className="w-6 h-6 rounded bg-base-800 border border-base-700 flex items-center justify-center overflow-hidden shrink-0">
                              {businessUnit?.logoUrl ? (
                                <img
                                  src={businessUnit.logoUrl}
                                  className="w-full h-full object-cover"
                                  alt=""
                                />
                              ) : (
                                <SafeIcon name="Briefcase" className="w-3 h-3 text-gray-600" />
                              )}
                            </div>

                            <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider truncate">
                              {businessUnit?.name || 'No Business Unit'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => handleArchive(project)}
                          disabled={isArchiving}
                          className="p-2 text-accent hover:bg-accent/10 rounded-lg transition-all disabled:opacity-50"
                          title="Archive Project"
                        >
                          <SafeIcon
                            name={isArchiving ? 'Loader' : 'Archive'}
                            className={`w-4 h-4 ${isArchiving ? 'animate-spin' : ''}`}
                          />
                        </button>

                        <button
                          onClick={() => handleCopyProject(project)}
                          className="p-2 text-gray-500 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-all"
                          title="Duplicate"
                        >
                          <SafeIcon name="Copy" className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => navigate(`/builder/${project.id}`)}
                          className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                          title="Edit"
                        >
                          <SafeIcon name="Edit2" className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => {
                            if (window.confirm('Delete project?')) {
                              deleteEntity('projects', project.id).catch((error) => {
                                console.error('Delete project error:', error);
                                alert(extractErrorMessage(error));
                              });
                            }
                          }}
                          className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                          title="Delete"
                        >
                          <SafeIcon name="Trash2" className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                      <div className="space-y-4">
                        {project.description && (
                          <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed italic border-l-2 border-base-700 pl-3">
                            {project.description}
                          </p>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <div className="text-[10px] uppercase text-gray-600 font-bold tracking-widest">
                              Start Date
                            </div>
                            <div className="text-xs font-mono text-gray-300">
                              {project.startDate}
                            </div>
                          </div>

                          <div className="space-y-1">
                            <div className="text-[10px] uppercase text-gray-600 font-bold tracking-widest">
                              Est. End
                            </div>
                            <div className="text-xs font-mono text-gray-300">
                              {project.calculatedEndDate}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-xl bg-base-900 border border-base-700">
                          <div className="space-y-1">
                            <div className="text-[10px] uppercase text-gray-600 font-bold tracking-widest">
                              Total Effort
                            </div>
                            <div className="text-sm font-mono font-bold text-accent">
                              {project.totalHours}h
                            </div>
                          </div>

                          <div className="h-8 w-px bg-base-700 mx-2" />

                          <div className="space-y-1 text-right">
                            <div className="text-[10px] uppercase text-gray-600 font-bold tracking-widest">
                              Status
                            </div>

                            <div className="flex items-center gap-2 justify-end">
                              <span className="text-[10px] font-bold text-gray-400 uppercase">
                                {project.isFullyComplete ? 'Ready' : 'Progress'}
                              </span>

                              <div
                                className="w-2 h-2 rounded-full animate-pulse"
                                style={{
                                  backgroundColor: getStatusColor(
                                    project.isFullyComplete ? 'completed' : 'in_progress'
                                  )
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="h-full">
                        {project.deadline ? (
                          <div
                            className={`h-full p-4 rounded-2xl flex flex-col justify-between border ${
                              isOverdue
                                ? 'bg-red-500/5 border-red-500/20'
                                : 'bg-green-500/5 border-green-500/20'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-[10px] text-gray-500 uppercase font-black tracking-widest">
                                Deadline Health
                              </div>

                              <div
                                className={`text-[9px] px-2 py-0.5 rounded-full font-black uppercase ${
                                  isOverdue
                                    ? 'bg-red-500/10 text-red-400'
                                    : 'bg-green-500/10 text-green-400'
                                }`}
                              >
                                {isOverdue ? 'At Risk' : 'On Track'}
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              <div
                                className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                                  isOverdue
                                    ? 'bg-red-500/10 text-red-400'
                                    : 'bg-green-500/10 text-green-400'
                                }`}
                              >
                                <SafeIcon
                                  name={isOverdue ? 'AlertTriangle' : 'CheckCircle'}
                                  className="w-6 h-6"
                                />
                              </div>

                              <div>
                                <div
                                  className={`text-2xl font-black leading-none ${
                                    isOverdue ? 'text-red-400' : 'text-green-400'
                                  }`}
                                >
                                  {varianceAbs} {varianceAbs === 1 ? 'Day' : 'Days'}
                                </div>

                                <div className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter mt-1">
                                  {isOverdue ? 'After Deadline' : 'Available Buffer'}
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                              <span className="text-[9px] text-gray-500 font-bold uppercase">
                                Target Date
                              </span>

                              <span className="text-[10px] font-mono font-bold text-white">
                                {project.deadline}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="h-full p-4 rounded-2xl bg-base-900/50 border border-base-700 border-dashed flex flex-col items-center justify-center text-center min-h-[140px]">
                            <SafeIcon name="Calendar" className="w-6 h-6 text-gray-700 mb-2" />
                            <div className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">
                              No Hard Deadline
                            </div>
                            <button
                              onClick={() => navigate(`/builder/${project.id}`)}
                              className="mt-2 text-[9px] text-primary hover:underline font-bold uppercase"
                            >
                              Set Goal
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="px-6 pb-6 mt-auto">
                      <div className="text-[9px] font-bold text-gray-600 uppercase tracking-widest mb-3 flex items-center gap-2">
                        <div className="h-px flex-1 bg-base-700" />
                        Timeline Sequence
                        <div className="h-px flex-1 bg-base-700" />
                      </div>

                      <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide py-1">
                        {project.scheduledWorkflows.map((workflow, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 rounded-xl bg-base-900 border border-base-700 flex items-center gap-3 shrink-0 shadow-sm group-hover:border-base-600 transition-colors"
                          >
                            <div
                              className="w-2.5 h-2.5 rounded-full shadow-sm border border-white/5 shrink-0"
                              style={{ backgroundColor: getStatusColor(workflow.calculatedStatus) }}
                            />
                            <span className="text-[10px] text-gray-400 font-bold truncate max-w-[100px]">
                              {workflow.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
};